/* reverse-image-search.js — send THIS picture to a search-by-image engine in
 * one tap, instead of a link to the engine's front page.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 * When a photo carries no coordinates — 94 % of the photos people bring to
 * /photo-location (RU, 30 days to 28.10.2026) — the only remaining answer to
 * "where was this taken" is in the pixels, and the engines that read pixels
 * best are Yandex and Google Lens. The tool used to give four links to their
 * HOME pages: on a phone that means leaving the page, finding the camera icon
 * and picking the same photo again from the gallery. Measured 29.10.2026:
 *
 *   • Yandex — its own uploader, `images-apphost/image-download`, answers a
 *     raw-JPEG POST from ANY origin with Access-Control-Allow-Origin echoed
 *     back (preflight 204, POST 200 with `cbir_id`), and
 *     `/images/search?rpt=imageview&cbir_id=…&url=…` opens the finished
 *     result page. The old `upfile` form POST lands on an empty
 *     "upload an image" page now — it is NOT an option.
 *   • Google Lens — a multipart form POST to `lens.google.com/v3/upload`
 *     (field `encoded_image`) answers 303 to the result page. That is a form
 *     submission, so the page's CSP `form-action` must name the host:
 *     SecurityHeaders adds exactly `https://lens.google.com` to pages that
 *     load this file (mountsReverseImageSearch).
 *   • TinEye answers 405 to a form POST and Bing's upload page renders
 *     blank — both stay plain links.
 *
 * What is sent is a re-drawn JPEG (longest side 1600 px): pixels only, no
 * EXIF/XMP, and 5–10× smaller than a phone original on a mobile uplink. The
 * caller's button names the engine, because the photo does leave for it.
 *
 * The tab is opened synchronously inside the click (a popup opened after an
 * await is blocked) and pointed at the result once the upload answers; any
 * failure sends it to the engine's own page rather than leaving it blank.
 *
 * Exposes `window.ReverseImageSearch`.
 * ═══════════════════════════════════════════════════════════════════════════ */
(function () {
    'use strict';

    var MAX_SIDE = 1600, QUALITY = 0.85;
    var LENS_UPLOAD = 'https://lens.google.com/v3/upload';

    function yandexHost(locale) { return locale === 'ru' ? 'yandex.ru' : 'yandex.com'; }

    var HOME = {
        yandex: function (l) { return 'https://' + yandexHost(l) + '/images/'; },
        lens: function () { return 'https://lens.google.com/'; },
    };

    /* Pixels only, at a size the engines need. A file the browser cannot
     * draw (HEIC outside Safari) goes as it is — the engine may still read it,
     * and a refused upload falls through to the engine's page below. */
    function prepare(src) {
        if (typeof createImageBitmap !== 'function') return Promise.resolve(src);
        return createImageBitmap(src, { imageOrientation: 'from-image' }).then(function (bmp) {
            var k = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
            var w = Math.max(1, Math.round(bmp.width * k)), h = Math.max(1, Math.round(bmp.height * k));
            var c = document.createElement('canvas');
            c.width = w; c.height = h;
            c.getContext('2d').drawImage(bmp, 0, 0, w, h);
            if (bmp.close) bmp.close();
            // The one door for canvas → Blob (timbrica-core.js): it always answers.
            if (!window.__canvasBlob) throw new Error('no_canvas_blob');
            return window.__canvasBlob(c, 'image/jpeg', QUALITY);
        }).catch(function () { return src; });
    }

    function waiting(win, text) {
        try {
            win.document.title = text;
            var p = win.document.createElement('p');
            p.textContent = text;
            p.style.cssText = 'font:16px/1.5 system-ui,sans-serif;margin:40px 16px;color:#555;text-align:center';
            win.document.body.appendChild(p);
        } catch (_) {}
    }

    function toYandex(win, blob, locale) {
        var host = yandexHost(locale);
        return fetch('https://' + host + '/images-apphost/image-download?cbird=111&images_avatars_size=preview&images_avatars_namespace=images-cbir', {
            method: 'POST', body: blob, credentials: 'omit',
            headers: { 'Content-Type': blob.type || 'image/jpeg' },
        }).then(function (r) {
            if (!r.ok) throw new Error('yandex_http_' + r.status);
            return r.json();
        }).then(function (j) {
            var id = j && j.cbir_id;
            var orig = j && j.sizes && j.sizes.orig && j.sizes.orig.path;
            if (!id || !orig) throw new Error('yandex_no_cbir');
            win.location.replace('https://' + host + '/images/search?rpt=imageview&cbir_id=' +
                encodeURIComponent(id) + '&url=' + encodeURIComponent(orig));
        });
    }

    /* The form is built INSIDE the tab we already opened (a same-origin
     * about:blank, so it needs no fresh user activation) and that document
     * declares no-referrer. Lens answers 403 to a POST whose Origin is another
     * site and 303 to `Origin: null` (measured 29.10.2026 with curl and in
     * Chromium); a non-GET request under no-referrer serialises its origin as
     * `null` (Fetch, "append a request Origin header"). ⚠️ `rel="noreferrer"`
     * on a form in OUR page did not do it — Chromium still sent our Origin and
     * Referer and got the 403. */
    function toLens(win, blob, locale) {
        if (typeof DataTransfer !== 'function') return Promise.reject(new Error('no_datatransfer'));
        var d = win.document;
        var meta = d.createElement('meta');
        meta.name = 'referrer';
        meta.content = 'no-referrer';
        d.head.appendChild(meta);
        var input = d.createElement('input');
        input.type = 'file';
        input.name = 'encoded_image';
        var dt = new DataTransfer();
        dt.items.add(new File([blob], 'photo.jpg', { type: blob.type || 'image/jpeg' }));
        input.files = dt.files;
        if (!input.files || !input.files.length) return Promise.reject(new Error('no_file_slot'));
        var form = d.createElement('form');
        form.method = 'POST';
        form.enctype = 'multipart/form-data';
        form.action = LENS_UPLOAD + '?hl=' + encodeURIComponent(locale || 'en');
        form.hidden = true;
        form.appendChild(input);
        d.body.appendChild(form);
        form.submit();
        return Promise.resolve();
    }

    var SEND = { yandex: toYandex, lens: toLens };

    /**
     * open(engine, image, {locale, waitText, slug}) — call from the click
     * handler itself. `image` is a File/Blob or anything createImageBitmap
     * reads (/video-location hands a canvas with the current frame). Resolves
     * {ok, engine, ms, bytes, reason?}; never rejects.
     */
    function open(engine, file, opts) {
        opts = opts || {};
        var locale = opts.locale || window.__LOCALE || 'en';
        var send = SEND[engine];
        var t0 = Date.now();
        var win = send ? window.open('', '_blank') : null;
        var done = function (res) {
            res.engine = engine; res.ms = Date.now() - t0;
            try {
                if (window.__ae) window.__ae('feature_use', { slug: opts.slug || null, f: 'rsearch', engine: engine, ok: res.ok ? 1 : 0, ms: res.ms, kb: res.bytes ? Math.round(res.bytes / 1024) : null, why: res.reason || null });
            } catch (_) {}
            return res;
        };
        if (!send) return Promise.resolve(done({ ok: false, reason: 'unknown_engine' }));
        if (!win) return Promise.resolve(done({ ok: false, reason: 'popup_blocked' }));
        try { win.opener = null; } catch (_) {}
        waiting(win, opts.waitText || '…');
        var bytes = 0;
        return prepare(file).then(function (blob) {
            bytes = blob.size;
            return send(win, blob, locale);
        }).then(function () {
            return done({ ok: true, bytes: bytes });
        }).catch(function (err) {
            try { win.location.replace(HOME[engine](locale)); } catch (_) {}
            return done({ ok: false, bytes: bytes, reason: String((err && err.message) || err).slice(0, 60) });
        });
    }

    window.ReverseImageSearch = { open: open, prepare: prepare, LENS_UPLOAD: LENS_UPLOAD };
})();
